import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance as axios } from '../context/AuthContext';
import { useAuth } from '../context/AuthContext';
import { Award, Gift, Users, Check, Star, DollarSign, UserPlus, Users2, ChevronDown, ChevronUp } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';

const API = '/api';

const PRO_MILESTONES = [
  { referrals: 1, label: '1st Referral', reward: '20% off next month' },
  { referrals: 3, label: '3rd Referral', reward: '1 free month' },
  { referrals: 5, label: '5th Referral', reward: 'Permanent 10% off' },
];

export function IncentivesSummary() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [proStats, setProStats] = useState(null);
  const [discount, setDiscount] = useState(null);
  const [familyReferralStats, setFamilyReferralStats] = useState(null);
  const [families, setFamilies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const [statsRes, discountRes, familiesRes] = await Promise.all([
        axios.get(`${API}/professional-invites/stats`, { withCredentials: true }).catch(() => null),
        axios.get(`${API}/subscriptions/available-discount`, { withCredentials: true }).catch(() => null),
        axios.get(`${API}/families`, { withCredentials: true }).catch(() => null),
      ]);
      if (statsRes?.data) setProStats(statsRes.data);
      if (discountRes?.data) setDiscount(discountRes.data);
      if (familiesRes?.data) {
        setFamilies(familiesRes.data);
        // Fetch family referral stats for the first family
        const activeFamilies = familiesRes.data.filter(f => !f.is_archived);
        if (activeFamilies.length > 0) {
          const famStatsRes = await axios.get(`${API}/family-referrals/stats/${activeFamilies[0].family_id}`, { withCredentials: true }).catch(() => null);
          if (famStatsRes?.data) setFamilyReferralStats(famStatsRes.data);
        }
      }
    } catch (err) {
      console.error('Failed to load incentive data:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return null;

  const proReferrals = proStats?.successful_referrals || 0;
  const famCompleted = familyReferralStats?.completed || 0;
  const famPending = familyReferralStats?.pending || 0;
  const totalReferrals = proReferrals + famCompleted;

  return (
    <div className="space-y-3" data-testid="incentives-summary">
      {/* Top row: Founding Member + Active Discount — compact side-by-side */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {user?.founding_member && (
          <Card className="border-amber-200 bg-gradient-to-r from-amber-50 to-yellow-50">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-yellow-500 flex items-center justify-center shrink-0">
                <Award className="text-white" size={18} />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h4 className="font-bold text-amber-900 text-sm">Founding Family Member</h4>
                  <Badge className="bg-green-100 text-green-700 border-green-200 text-xs">Active</Badge>
                </div>
                <p className="text-amber-700 text-xs mt-0.5">Permanent badge for early supporters</p>
              </div>
            </CardContent>
          </Card>
        )}

        {discount?.has_discount && (
          <Card className="border-green-200 bg-green-50">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-200 flex items-center justify-center shrink-0">
                <DollarSign className="text-green-700" size={18} />
              </div>
              <div className="min-w-0">
                <h4 className="font-bold text-green-900 text-sm">{discount.discount_percent}% Off Active</h4>
                <p className="text-green-700 text-xs">{discount.discount_reason}</p>
                {discount.is_permanent ? (
                  <Badge className="bg-green-200 text-green-800 text-xs mt-0.5">Permanent</Badge>
                ) : discount.expires_at && (
                  <p className="text-xs text-green-600 mt-0.5">Expires {new Date(discount.expires_at).toLocaleDateString()}</p>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Referral Summary Card — Compact, expandable */}
      <Card className="border-0 shadow-md">
        <CardContent className="p-4">
          <button
            onClick={() => setExpanded(!expanded)}
            className="w-full flex items-center justify-between"
            data-testid="toggle-referral-details"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center shrink-0">
                <Gift className="text-white" size={18} />
              </div>
              <div className="text-left">
                <h4 className="font-bold text-gray-900 text-sm">Referral Rewards</h4>
                <p className="text-gray-500 text-xs">
                  {totalReferrals} successful referral{totalReferrals !== 1 ? 's' : ''} total
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {/* Quick stats inline */}
              <div className="hidden sm:flex items-center gap-2 text-xs">
                <span className="flex items-center gap-1 bg-blue-50 text-blue-700 px-2 py-1 rounded-full">
                  <UserPlus size={12} /> Pro: {proReferrals}
                </span>
                <span className="flex items-center gap-1 bg-teal-50 text-teal-700 px-2 py-1 rounded-full">
                  <Users2 size={12} /> Family: {famCompleted}
                </span>
              </div>
              {expanded ? <ChevronUp size={18} className="text-gray-400" /> : <ChevronDown size={18} className="text-gray-400" />}
            </div>
          </button>

          {expanded && (
            <div className="mt-4 space-y-4">
              {/* Quick Stats Grid */}
              <div className="grid grid-cols-4 gap-2">
                <div className="text-center p-2 bg-blue-50 rounded-lg">
                  <p className="text-lg font-bold text-blue-700">{proStats?.total_invites_sent || 0}</p>
                  <p className="text-[10px] text-blue-600">Pro Invites</p>
                </div>
                <div className="text-center p-2 bg-green-50 rounded-lg">
                  <p className="text-lg font-bold text-green-700">{proReferrals}</p>
                  <p className="text-[10px] text-green-600">Pro Joined</p>
                </div>
                <div className="text-center p-2 bg-teal-50 rounded-lg">
                  <p className="text-lg font-bold text-teal-700">{familyReferralStats?.total_sent || 0}</p>
                  <p className="text-[10px] text-teal-600">Fam Invites</p>
                </div>
                <div className="text-center p-2 bg-emerald-50 rounded-lg">
                  <p className="text-lg font-bold text-emerald-700">{famCompleted}</p>
                  <p className="text-[10px] text-emerald-600">Fam Joined</p>
                </div>
              </div>

              {/* Milestones — Compact horizontal */}
              <div>
                <p className="text-xs font-medium text-gray-600 mb-2">Professional Referral Milestones</p>
                <div className="flex gap-2">
                  {PRO_MILESTONES.map((ms, idx) => {
                    const achieved = proReferrals >= ms.referrals;
                    return (
                      <div
                        key={idx}
                        className={`flex-1 p-2 rounded-lg border text-center ${
                          achieved ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
                        }`}
                      >
                        <p className={`text-xs font-medium ${achieved ? 'text-green-800' : 'text-gray-600'}`}>{ms.label}</p>
                        <p className={`text-[10px] ${achieved ? 'text-green-600' : 'text-gray-500'}`}>{ms.reward}</p>
                        {achieved && <Check size={12} className="text-green-600 mx-auto mt-1" />}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Family Referral Reward */}
              <div className="bg-teal-50 border border-teal-200 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-teal-800">Family Referral Reward</p>
                    <p className="text-[10px] text-teal-600">20% off next month per family that joins</p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-teal-700">{famCompleted}</p>
                    <p className="text-[10px] text-teal-600">earned</p>
                  </div>
                </div>
              </div>

              {/* Badges */}
              {proStats?.badges?.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {proStats.badges.map((badge, idx) => (
                    <Badge key={idx} className="bg-purple-100 text-purple-700 text-xs px-2 py-0.5">
                      <Star className="h-3 w-3 mr-1" /> {badge}
                    </Badge>
                  ))}
                </div>
              )}

              {/* CTAs */}
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => navigate('/my-invitations')}
                  className="flex items-center justify-center gap-1.5 px-3 py-2 bg-gradient-to-r from-purple-500 to-indigo-600 text-white rounded-lg hover:from-purple-600 hover:to-indigo-700 font-medium text-sm transition"
                  data-testid="go-to-invitations-btn"
                >
                  <UserPlus size={14} />
                  Invite Professional
                </button>
                <button
                  onClick={() => {
                    // Scroll to top and trigger the refer modal from parent
                    window.dispatchEvent(new CustomEvent('openReferFamily'));
                  }}
                  className="flex items-center justify-center gap-1.5 px-3 py-2 bg-gradient-to-r from-teal-500 to-emerald-600 text-white rounded-lg hover:from-teal-600 hover:to-emerald-700 font-medium text-sm transition"
                  data-testid="refer-family-from-incentives-btn"
                >
                  <Users2 size={14} />
                  Refer a Family
                </button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default IncentivesSummary;
